/**
 * Canonical role vocabulary — docs/ROLE-MATRIX.md §1 (ratified joint contract,
 * #595). The ONLY roles either lane may gate on are NU Core's six. Everything
 * else — including the design kit's 15-role set and this repo's legacy
 * `customer`/`editor` session roles — is DENIED fail-closed until a mapping is
 * ratified through joint change control (legacy→canonical mapping tracked in
 * NU-DATA-UI#641).
 *
 * Do NOT add aliases here beyond what ROLE-MATRIX §2 itself ratifies.
 */

export const CANONICAL_ROLES = [
  "owner",
  "admin",
  "member",
  "viewer",
  "billing",
  "tenant_admin",
] as const;

export type CanonicalRole = (typeof CANONICAL_ROLES)[number];

const CANONICAL_ROLE_SET: ReadonlySet<string> = new Set(CANONICAL_ROLES);

/**
 * ROLE-MATRIX §2: design-system → canonical mapping. `billing` and `admin`
 * coincide with canonical names (identity, no alias entry needed); `user` is
 * the only genuine rename the contract ratifies.
 */
export const RATIFIED_ALIASES: Readonly<Record<string, CanonicalRole>> = {
  user: "member",
};

/**
 * Role names known from superseded layers (the kit's 15-role rbac.js set, its
 * aliases, and this repo's legacy Lane-A session roles) whose canonical
 * mapping is NOT ratified. They are denied with a distinct reason code so the
 * UI can, later, act on it (e.g. prompt re-login once #641 lands a mapping).
 * NOTE: `customer` ≠ `member` (portal-pinned semantics) — never map it here.
 */
export const UNRATIFIED_LEGACY_ROLES: ReadonlySet<string> = new Set([
  // legacy Lane-A session roles (useAuth UserRole) without a ratified mapping
  "customer",
  "editor",
  // kit rbac.js 15-role set (minus names that coincide with canonical)
  "master_admin",
  "platform_admin",
  "org_admin",
  "data_manager",
  "campaign_manager",
  "product_manager",
  "enrichment_user",
  "readonly",
  "web_customer",
  "api_customer",
  "api_user",
  // kit rbac.js alias slugs
  "super_admin",
  "superadmin",
  // ROLE-MATRIX §4: reseller does not exist in any enforceable layer
  "reseller",
]);

export type RoleDenialReason =
  "NO_ROLE" | "LEGACY_ROLE_UNRATIFIED" | "UNKNOWN_ROLE";

export type NormalizeRoleResult =
  | { ok: true; role: CanonicalRole }
  | { ok: false; reason: RoleDenialReason; input: string | null };

/**
 * Translate a raw role claim to the canonical vocabulary — the "exactly one
 * place per lane" of ROLE-MATRIX §1. FAIL-CLOSED: anything that is neither
 * canonical nor a ratified alias is rejected (never passed through, unlike the
 * kit's normalizeRole).
 */
export function normalizeRole(
  input: string | null | undefined,
): NormalizeRoleResult {
  if (input === null || input === undefined || input.trim() === "") {
    return { ok: false, reason: "NO_ROLE", input: input ?? null };
  }
  const raw = input.trim().toLowerCase();
  if (CANONICAL_ROLE_SET.has(raw)) {
    return { ok: true, role: raw as CanonicalRole };
  }
  // Object.hasOwn guards against Object.prototype keys ("constructor",
  // "__proto__") leaking through the plain-object alias table as ok:true.
  if (Object.hasOwn(RATIFIED_ALIASES, raw)) {
    return { ok: true, role: RATIFIED_ALIASES[raw] };
  }
  if (UNRATIFIED_LEGACY_ROLES.has(raw)) {
    return { ok: false, reason: "LEGACY_ROLE_UNRATIFIED", input };
  }
  return { ok: false, reason: "UNKNOWN_ROLE", input };
}
